import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["wrapperStyle", "onChange", "isValid", "value", "disabled", "placeholder", "checkmarkTop", "checkmarkRight", "options", "id"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { animated, useSpring } from '@react-spring/web';
import { PropTypes } from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { ToggleLayer } from 'react-laag';
import { useVirtual } from 'react-virtual';
import ROPolyfill from 'resize-observer-polyfill';
import styled from 'styled-components';
import { useMediaQuery } from '../../../hooks';
import { Icon } from '../../Icon';
import { ChevronDown } from '../../Icons';
import { Box } from '../../Layout';
import { Text } from '../../Typography';
import { ValidCheckmark } from '../ValidCheckmark';
import { GenderOption, GenderRotationBox, InputWrapper, OptionsContainer, TriggerWrapper } from './styledComponents';
const duration = 200;
const StyledInput = styled.input.withConfig({
  displayName: "StyledInput",
  componentId: "sc-i8yuhi-0"
})(["&&&{padding:0;border:none;width:100%;font-size:inherit;line-height:inherit;font-weight:900;background-color:inherit;transition:padding ", "ms ease-in;::placeholder{font-weight:400;}:hover{cursor:pointer;}}"], duration);
const StyledContainer = styled.div.withConfig({
  displayName: "StyledContainer",
  componentId: "sc-i8yuhi-1"
})(["position:", ";width:100%;"], props => props.isMobile ? 'initial' : 'relative');
export const GenderInput = React.forwardRef(function GenderInput(props, ref) {
  const {
      wrapperStyle,
      onChange,
      isValid,
      value,
      disabled,
      placeholder,
      checkmarkTop,
      checkmarkRight,
      options,
      id
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const [isOpen, setIsOpen] = useState(false);
  const onSelect = selection => {
    onChange(selection.slug);
    setIsOpen(false);
  };
  const toggle = () => {
    setIsOpen(!isOpen);
  };
  const RotationBox = animated(Box);
  const isMobile = useMediaQuery('mobile');
  let ResizeObserver;
  if (typeof window !== 'undefined') {
    ResizeObserver = window.ResizeObserver || ROPolyfill;
  }
  const valueDisplay = value ? options.filter(option => option.slug === value)[0].displayName : value;
  return React.createElement(StyledContainer, {
    className: id,
    isMobile: isMobile
  }, React.createElement(ToggleLayer, {
    disabled: disabled,
    renderLayer: _ref => {
      let {
        layerProps,
        close
      } = _ref;
      const handleSelect = selection => {
        onSelect === null || onSelect === void 0 || onSelect(selection);
        close();
      };
      let styleOptionContainer = _objectSpread({}, layerProps.style);
      if (!isMobile) styleOptionContainer = _objectSpread(_objectSpread({}, styleOptionContainer), {}, {
        left: 0,
        top: '37px'
      });else styleOptionContainer = _objectSpread(_objectSpread({}, styleOptionContainer), {}, {
        left: 0,
        top: 'inherit',
        bottom: 0
      });
      return isOpen && !disabled && React.createElement(OptionsContainer, {
        bg: "brandLight",
        borderRadius: 3,
        maxWidth: "100%",
        overflowX: "hidden",
        boxShadow: "0 -14px 36px 0 rgba(75, 80, 94, 0.08)",
        ref: layerProps.ref,
        style: styleOptionContainer
      }, React.createElement(OptionsVirtualized, {
        options: options,
        onClick: handleSelect
      }));
    },
    fixed: isMobile,
    closeOnOutsideClick: true,
    placement: {
      possibleAnchors: ['BOTTOM_CENTER', 'BOTTOM_LEFT'],
      anchor: isMobile ? 'BOTTOM_CENTER' : 'BOTTOM_LEFT',
      autoAdjust: true,
      triggerOffset: 22,
      scrollOffset: 0
    },
    ResizeObserver: ResizeObserver,
    container: () => isMobile ? null : document.querySelector(".".concat(id))
  }, _ref2 => {
    let {
      triggerRef,
      isOpen
    } = _ref2;
    const iconSpring = useSpring({
      transform: isOpen && !disabled ? 'rotate(180deg)' : 'rotate(0deg)'
    });
    return React.createElement(TriggerWrapper, _extends({}, wrapperStyle, {
      marginRight: 2,
      ref: triggerRef,
      onClick: () => toggle()
    }), React.createElement(InputWrapper, {
      onClick: () => toggle()
    }, React.createElement(StyledInput, _extends({
      ref: ref,
      color: "inherit",
      placeholder: placeholder,
      isValid: isValid,
      value: valueDisplay,
      disabled: disabled,
      readOnly: true
    }, rest)), React.createElement(GenderRotationBox, {
      isValid: isValid
    }, React.createElement(RotationBox, {
      style: iconSpring,
      width: "min-content"
    }, React.createElement(Icon, {
      color: "brandDark",
      fontSize: 1,
      isPointer: true
    }, React.createElement(ChevronDown, null)))), React.createElement(ValidCheckmark, {
      isValid: isValid,
      top: checkmarkTop,
      right: checkmarkRight
    })));
  }));
});
const OPTION_HEIGHT = 48;
const OptionsVirtualized = _ref3 => {
  var _window;
  let {
    options,
    onClick
  } = _ref3;
  const parentRef = useRef();
  const estimateSize = useCallback(() => OPTION_HEIGHT, []);
  const isMobile = useMediaQuery('mobile');
  const WIDTH = isMobile ? (_window = window) === null || _window === void 0 ? void 0 : _window.innerWidth : 400;
  const {
    virtualItems,
    totalSize
  } = useVirtual({
    size: options.length,
    parentRef,
    estimateSize,
    overscan: 10
  });
  return React.createElement(Box, {
    width: "".concat(WIDTH, "px"),
    height: "auto",
    overflow: "auto",
    ref: parentRef
  }, React.createElement(Box, {
    position: "relative",
    height: totalSize,
    width: "100%"
  }, virtualItems.map(virtualItem => {
    const option = options[virtualItem.index];
    return React.createElement(GenderOption, {
      key: virtualItem.index,
      size: virtualItem.size,
      start: virtualItem.start,
      innerWidth: "".concat(WIDTH - 36, "px"),
      onClick: () => onClick(option)
    }, React.createElement(Text, {
      marginRight: 2,
      wrapped: true
    }, option.displayName));
  })));
};
GenderInput.propTypes = process.env.NODE_ENV !== "production" ? {
  placeholder: PropTypes.string,
  isValid: PropTypes.bool,
  wrapperStyle: PropTypes.objectOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  checkmarkRight: PropTypes.string,
  checkmarkTop: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string,
    displayName: PropTypes.string,
    id: PropTypes.string
  }).isRequired)
} : {};
GenderInput.defaultProps = {
  isValid: false,
  wrapperStyle: {
    border: 'brandDark',
    borderRadius: '24px',
    backgroundColor: 'background.spaceWhite',
    padding: '13px 18px',
    fontSize: '17px',
    lineHeight: '21px'
  },
  disabled: false
};